import { useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import type { TypeCurveHeader } from 'types/api-responses';
import {
  tcMapFilterModeAtom,
  tcMapBoundsAtom,
  tcAoiPolygonAtom,
  tcAoiPolygonLockedAtom,
} from './atoms';
import { MODE_BY_WCA_KIND } from './well-control-area';

// Preset the Global Map Filter from the persisted Well Control Area on the
// header response. Runs once per header id (so it re-seeds when switching
// workspace/header but never clobbers the user's in-session filter changes on
// re-render). A null `wellControlAreaKind` leaves the default 'type-curve-area'.
export function useWellControlAreaInit(header: TypeCurveHeader | null): void {
  const setMode = useSetAtom(tcMapFilterModeAtom);
  const setBounds = useSetAtom(tcMapBoundsAtom);
  const setPolygon = useSetAtom(tcAoiPolygonAtom);
  const setPolygonLocked = useSetAtom(tcAoiPolygonLockedAtom);
  const initedForRef = useRef<number | null>(null);

  const headerId = header?.id ?? null;
  const kind = header?.wellControlAreaKind ?? null;

  useEffect(() => {
    if (headerId === null || initedForRef.current === headerId) return;
    initedForRef.current = headerId;
    if (!kind) return;

    setMode(MODE_BY_WCA_KIND[kind]);

    if (kind === 'map_extent') {
      const bbox = header?.wellControlAreaBbox;
      if (bbox && bbox.length === 4) {
        setBounds({
          sw_lng: bbox[0],
          sw_lat: bbox[1],
          ne_lng: bbox[2],
          ne_lat: bbox[3],
        });
      }
    } else if (kind === 'aoi_polygon') {
      const geojson = header?.wellControlAreaGeometryAsGeojson;
      if (geojson) {
        setPolygon(geojson);
        setPolygonLocked(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerId, kind]);
}
